import React, { useState } from "react";
import { searchStudentByCnic } from "../api/public";

const CnicSearch = () => {
  const [cnic, setCnic] = useState("");
  const [result, setResult] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSearch = async (e) => {
    e.preventDefault();
    const trimmed = cnic.trim();
    if (!trimmed) return;

    setLoading(true);
    setResult(null);
    setNotFound(false);
    setError("");

    try {
      const data = await searchStudentByCnic(trimmed);
      setResult(data.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setNotFound(true);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container py-5" style={{ maxWidth: "760px" }}>
      <h1 className="font-40 text-center mb-2">Student Record Search</h1>
      <p className="text-center text-muted mb-4" style={{ fontSize: "15px" }}>
        Enter your CNIC number below to find your registration details.
      </p>

      {/* Search Bar */}
      <form onSubmit={handleSearch} className="d-flex gap-2 mb-4">
        <input
          type="text"
          id="cnic-search-input"
          className="form-control"
          placeholder="Enter CNIC (e.g. 3520212345678)"
          value={cnic}
          onChange={(e) => setCnic(e.target.value)}
          style={{ fontSize: "15px", padding: "10px 14px" }}
          required
        />
        <button
          id="cnic-search-btn"
          type="submit"
          className="btn btn-success px-4 fw-semibold"
          disabled={loading}
          style={{ whiteSpace: "nowrap" }}
        >
          {loading ? "Searching..." : "Search"}
        </button>
      </form>

      {/* Error */}
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* Not Found */}
      {notFound && (
        <div className="alert alert-warning text-center" role="alert">
          No record found for the entered CNIC.
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="card border rounded shadow-sm bg-white p-4">
          <h5 className="fw-bold mb-3 text-success">Student Details</h5>
          <table className="table table-bordered table-sm mb-0">
            <tbody>
              <tr>
                <td className="fw-semibold text-muted" style={{ width: "40%" }}>Full Name</td>
                <td>{result.fullName}</td>
              </tr>
              <tr>
                <td className="fw-semibold text-muted">Father's Name</td>
                <td>{result.fatherName}</td>
              </tr>
              <tr>
                <td className="fw-semibold text-muted">CNIC</td>
                <td>{result.cnic}</td>
              </tr>
              <tr>
                <td className="fw-semibold text-muted">Roll Number</td>
                <td>{result.rollNumber || "—"}</td>
              </tr>
              <tr>
                <td className="fw-semibold text-muted">Mobile</td>
                <td>{result.mobile}</td>
              </tr>
              <tr>
                <td className="fw-semibold text-muted">Email</td>
                <td>{result.email}</td>
              </tr>
              <tr>
                <td className="fw-semibold text-muted">Gender</td>
                <td>{result.gender}</td>
              </tr>
              <tr>
                <td className="fw-semibold text-muted">Date of Birth</td>
                <td>{result.dateOfBirth ? new Date(result.dateOfBirth).toLocaleDateString("en-PK") : "—"}</td>
              </tr>
              <tr>
                <td className="fw-semibold text-muted">Qualification</td>
                <td>{result.qualification}</td>
              </tr>
              <tr>
                <td className="fw-semibold text-muted">City</td>
                <td>{result.city}</td>
              </tr>
              <tr>
                <td className="fw-semibold text-muted">Permanent Address</td>
                <td>{result.permanentAddress}</td>
              </tr>
              <tr>
                <td className="fw-semibold text-muted">Enrolled Courses</td>
                <td>{result.courses?.length ? result.courses.join(", ") : "—"}</td>
              </tr>
              <tr>
                <td className="fw-semibold text-muted">Admission Type</td>
                <td>{result.admissionType?.join(", ") || "—"}</td>
              </tr>
              <tr>
                <td className="fw-semibold text-muted">Verified</td>
                <td>
                  <span className={`badge ${result.isVerified ? "bg-success" : "bg-secondary"}`}>
                    {result.isVerified ? "Yes" : "No"}
                  </span>
                </td>
              </tr>
              {/* Challan Status - only for online students */}
              {result.registrationSource !== "Onsite" && (
                <tr>
                  <td className="fw-semibold text-muted">Challan Status</td>
                  <td>
                    {result.challanStatus !== undefined ? (
                      <span className={`badge ${result.challanStatus === true ? "bg-success" : "bg-warning text-dark"}`}>
                        {result.challanStatus === true ? "Paid" : "Unpaid"}
                      </span>
                    ) : (
                      <span className="badge bg-secondary">No Challan</span>
                    )}
                  </td>
                </tr>
              )}
              {/* Test Passed - only for online students */}
              {result.registrationSource !== "Onsite" && (
                <tr>
                  <td className="fw-semibold text-muted">Test Passed</td>
                  <td>
                    <span className={`badge ${result.testPassed ? "bg-success" : "bg-danger"}`}>
                      {result.testPassed ? "Yes" : "No"}
                    </span>
                  </td>
                </tr>
              )}
              <tr>
                <td className="fw-semibold text-muted">Registration Source</td>
                <td>
                  <span className={`badge ${result.registrationSource === "Onsite" ? "bg-info text-dark" : "bg-primary text-white"}`}>
                    {result.registrationSource || "Online"}
                  </span>
                </td>
              </tr>
              <tr>
                <td className="fw-semibold text-muted">Registered On</td>
                <td>{result.createdAt ? new Date(result.createdAt).toLocaleDateString("en-PK") : "—"}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CnicSearch;
